package org.example.project.enginevertrieb

import androidx.compose.runtime.*
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.example.project.enginetabular.TabularScreen
import org.example.project.utils.All
import org.example.project.dashboard.PopupEngineCard
import org.example.project.dashboard.POPUP_ENGINE_MODAL_Z_INDEX
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.css.*
import org.w3c.dom.HTMLElement

@Composable
internal fun LeadInfoReadOnlyPopup(lead: All, onClose: () -> Unit) {
    PopupEngineCard(
        windowKey = "vertrieb-lead-info-${lead.id}", zIndex = POPUP_ENGINE_MODAL_Z_INDEX,
        widthPx = 760, heightPx = 560, minWidthPx = 420, minHeightPx = 320,
        centered = true, resizable = true, onClose = onClose
    ) {
        LeadInfoTable(lead, enabled = false, onUpdated = {})
    }
}

@Composable
internal fun LeadInfoTable(
    lead: All,
    enabled: Boolean,
    showComposer: Boolean = true,
    rollenKey: String = "Vertriebsindex",
    screenKey: String = "Vertriebsindex_Informationen_${lead.id}",
    addInfo: suspend (All, String) -> All? = { current, text -> VertriebsindexApi.addInfo(current.id, text) },
    deleteInfo: suspend (All, String) -> All? = { current, infoId -> VertriebsindexApi.deleteInfo(current.id, infoId) },
    onUpdated: (All) -> Unit
) {
    val legacy = lead.akquiseNotizen.takeIf { it.isNotBlank() }?.let {
        All(id = "legacy-note", leadInfoText = it, erstelltVon = "Bisherige Notiz")
    }
    val savedEntries = listOfNotNull(legacy) + lead.leadInfos
    val entries = savedEntries.ifEmpty { listOf(All(id = "empty-info", leadInfoText = "Keine Information hinterlegt")) }
    Div({ style { height(100.percent); display(DisplayStyle.Flex); flexDirection(FlexDirection.Column); gap(12.px); padding(18.px); boxSizing("border-box") } }) {
        Div({ style { flex("1"); minHeight(0.px) } }) {
            TabularScreen(
                screenKey = screenKey, rollenKey = rollenKey, title = "Informationen · ${lead.firma}",
                headers = listOf("Label", "Erstellt von", "Erstellt am", "Erstellt um"),
                data = entries.map { info ->
                    val (date, time) = leadInfoDateTime(info)
                    info.id to listOf(info.leadInfoText, info.erstelltVon, date, time)
                },
                editable = false, hardReadOnly = !enabled, includeMetadata = false,
                nonEditableCols = setOf("Label", "Erstellt von", "Erstellt am", "Erstellt um"),
                allowCreate = false, allowSave = false, allowDelete = enabled,
                isDeleteEnabled = { selected -> selected.isNotEmpty() && "legacy-note" !in selected && "empty-info" !in selected },
                allowImportButton = false, allowSettingsButton = false,
                showToolbarExtras = false,
                onDeleteRows = { ids ->
                    var latest = lead
                    ids.forEach { id -> latest = deleteInfo(latest, id) ?: kotlin.error("Information konnte nicht gelöscht werden.") }
                    onUpdated(latest)
                }, onCommit = {}
            )
        }
        if (showComposer) LeadInfoComposer(lead, enabled, onUpdated, addInfo = addInfo)
    }
}

@Composable
internal fun LeadInfoNotes(lead: All, enabled: Boolean, popupErrors: Boolean = false,
    addInfo: suspend (All, String) -> All? = { current, text -> VertriebsindexApi.addInfo(current.id, text) }, onUpdated: (All) -> Unit) {
    val entries = buildList {
        if (lead.akquiseNotizen.isNotBlank()) add(lead.akquiseNotizen)
        addAll(lead.leadInfos.map { it.leadInfoText })
    }
    LaunchedEffect(lead.id, entries.size) {
        val list = document.getElementById("akquise-notes-${lead.id}") as? HTMLElement
        if (list != null) list.scrollTop = list.scrollHeight.toDouble()
    }
    Div({ style {
        height(100.percent); minHeight(0.px); display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column); boxSizing("border-box")
    } }) {
        Div({
            id("akquise-notes-${lead.id}")
            style {
                flex("1"); minHeight(0.px); overflowY("auto")
                display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
                padding(0.px, 2.px); boxSizing("border-box")
            }
        }) {
            entries.forEachIndexed { index, entry ->
                Div({ style {
                    width(100.percent); padding(12.px); boxSizing("border-box")
                    fontSize(14.px); lineHeight("1.45")
                    backgroundColor(Color(if (index % 2 == 0) "rgba(255,255,255,.055)" else "rgba(255,255,255,.025)"))
                    property("border-bottom", "1px solid rgba(255,255,255,.14)")
                    property("white-space", "pre-wrap"); property("overflow-wrap", "anywhere")
                } }) { Text(entry) }
            }
        }
        Div({ style {
            flexShrink(0); padding(10.px, 2.px, 0.px)
            property("border-top", "1px solid rgba(255,255,255,.18)")
        } }) {
            LeadInfoComposer(lead, enabled, onUpdated, showWhenDisabled = true, popupErrors = popupErrors, addInfo = addInfo)
        }
    }
}

@Composable
internal fun LeadInfoComposer(
    lead: All, enabled: Boolean, onUpdated: (All) -> Unit, showWhenDisabled: Boolean = false,
    popupErrors: Boolean = false,
    addInfo: suspend (All, String) -> All? = { current, text -> VertriebsindexApi.addInfo(current.id, text) }
) {
    val scope = rememberCoroutineScope()
    var text by remember(lead.id) { mutableStateOf("") }
    var busy by remember(lead.id) { mutableStateOf(false) }
    var error by remember(lead.id) { mutableStateOf("") }
    LaunchedEffect(error) { if (popupErrors && error.isNotBlank()) { org.example.project.dashboard.AppDialogs.alert(error); error = "" } }
    if (!popupErrors && enabled && error.isNotBlank()) Div({ attr("role", "alert") }) { Text(error) }
    if (enabled || showWhenDisabled) Div({ style { display(DisplayStyle.Flex); gap(8.px) } }) {
            Input(InputType.Text, attrs = {
                value(text); attr("placeholder", "Text schreiben")
                if (!enabled || busy) attr("disabled", "true")
                onInput { text = it.value }
                style {
                    flex("1"); minWidth(0.px); padding(10.px); boxSizing("border-box")
                    backgroundColor(Color("#3F454E")); color(Color.white)
                    property("border", "1px solid rgba(255,255,255,.18)")
                    borderRadius(6.px); fontSize(14.px)
                }
            })
            Div({
                attr("role", "button")
                attr("aria-label", "Absenden")
                attr("title", "Absenden")
                attr("tabindex", if (enabled && !busy && text.isNotBlank()) "0" else "-1")
                if (enabled && !busy && text.isNotBlank()) onClick {
                    busy = true; error = ""
                    scope.launch {
                        try {
                            val updated = addInfo(lead, text.trim()) ?: kotlin.error("Information konnte nicht gespeichert werden.")
                            onUpdated(updated); text = ""
                        } catch (e: Exception) { error = e.message ?: "Speichern fehlgeschlagen." }
                        finally { busy = false }
                    }
                }
                style {
                    width(38.px); height(38.px); flexShrink(0)
                    display(DisplayStyle.Flex); alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
                    property("cursor", if (enabled && !busy && text.isNotBlank()) "pointer" else "default")
                    property("opacity", if (enabled && !busy && text.isNotBlank()) "0.92" else "0.4")
                    property("border", "none"); property("background", "transparent")
                }
            }) {
                Img(src = "images/send.svg", attrs = {
                    attr("alt", "")
                    style { width(22.px); height(22.px); property("filter", "brightness(0) invert(1)") }
                })
            }
    }
}

private fun leadInfoDateTime(info: All): Pair<String, String> {
    if (info.erstelltAm.isNotBlank() || info.erstelltUm.isNotBlank()) return info.erstelltAm to info.erstelltUm
    if (info.createdAt.isBlank()) return "" to ""
    val date = kotlin.js.Date(info.createdAt)
    if (date.getTime().isNaN()) return info.createdAt to ""
    return date.toLocaleDateString("de-DE") to date.toLocaleTimeString("de-DE", js("({hour: '2-digit', minute: '2-digit', hour12: false})"))
}

